import { Component, Inject, PLATFORM_ID, inject } from '@angular/core';
import { NotFoundService } from './not-found/not-found.service';
import { AttributionService } from './services/attribution.service';
import { ActivatedRoute, Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { ApiService } from './services/api.service';
import { VisitorTrackingService } from './services/visitor-tracking.service';
import { NavigationStart, NavigationEnd, NavigationError, Event as NavigationEvent } from '@angular/router';
import { SeoService } from './services/seo.service';

import { filter } from 'rxjs/operators';
import { SeoServiceTsService } from './seo-service';
import { isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TagConsentService } from './consent/tag-consent.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})

export class AppComponent {
  title = 'website';
  private notFound = inject(NotFoundService);
  private attribution = inject(AttributionService);
  initTime!: number;
  private startTime: number = 0;
  private visitorId: string = '';

  private static globalListenersAttached = false;

  pollTimerId?: ReturnType<typeof setInterval>;
  updateAvailable: boolean = false;

  private lastTrackedPageUrl = '';

  constructor(private router: Router,
    private seoService: SeoServiceTsService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private titleService: Title,
    private metaService: Meta,
    private visitorTrackingService: VisitorTrackingService,
    private apiService: ApiService,
    private updates: SwUpdate,
    private _snackBar: MatSnackBar,
    private tagConsentService: TagConsentService) {
    this.startTime = performance.now();

    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => {
        console.error('[Router] NavigationError - failed URL:', event.url, 'triggered from:', document.activeElement, event.error);
      });

    this.attribution.recordFirstTouch();

    // A 404 marks the page noindex; a later in-app navigation must not inherit that.
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => this.notFound.clear());

    // Page titles and descriptions are set by each page component: a NavigationEnd subscription here
    // misses the first, server-rendered navigation, so it never reached Google.

    if (isPlatformBrowser(this.platformId) && this.updates.isEnabled) {
      // Listen for ALL Service Worker events
      this.updates.versionUpdates
  .pipe(
    filter(
      (event): event is VersionReadyEvent =>
        event.type === 'VERSION_READY'
    )
  )
  .subscribe(() => {

    // Prevent showing the snackbar multiple times
    if (this.updateAvailable) {
      return;
    }

    this.updateAvailable = true;

    // this._snackBar.open(
    //   'A new version of Atmavaan is available. Refresh to use the latest improvements.',
    //   'Refresh',
    //   {
    //     horizontalPosition: 'center',
    //     verticalPosition: 'top',
    //     panelClass: ['update-snackbar']
    //   }
    // )
    // .onAction()
    // .subscribe(() => {
    //   this.updateApp();
    // });

  });
      // a new version that can't install never reaches VERSION_READY, so the refresh below never runs
      this.updates.versionUpdates
        .pipe(filter(event => event.type === 'VERSION_INSTALLATION_FAILED'))
        .subscribe(event => console.error('[SW] New version failed to install', event));
      // the running version's files are gone from the server: reload to get the deployed one
      this.updates.unrecoverable.subscribe(() => document.location.reload());
      // Initial check
      this.updates.checkForUpdate().catch(err =>
        console.error('[SW] Initial update check failed', err)
      );

      // Poll every 5 minutes
      this.pollTimerId = setInterval(async () => {
        try {
          await this.updates.checkForUpdate();
        } catch (err) {
          console.error('[SW] Poll Error:', err);
        }
      }, 5 * 60 * 1000);
      this.router.events
  .pipe(
    filter((event): event is NavigationStart => event instanceof NavigationStart)
  )
  .subscribe((event) => {

    if (this.updateAvailable) {
      // load the page the visitor is going to, not the one they're leaving
      this.updateApp(event.url);
    }

  });
    }
    }

  async ngOnInit() {
    this.visitorTrackingService.trackEmailClick();
    // Third-party tags follow the visitor's cookie choice.
    this.tagConsentService.start();
    this.consoleWarn();
    
    this.seoService.setCanonicalURL();

    // this.router.events.subscribe(event => {
    //   if (event instanceof NavigationEnd) {
    //     this.apiService.setCanonicalURL(); // Update canonical on navigation
    //   }
    // });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.seoService.setCanonicalURL(); // Update canonical on navigation
        this.logPageView(event.urlAfterRedirects);
      }
    });

    this.router.events
      .pipe(
        filter((event: NavigationEvent): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        this.seoService.addSchemaForUrl(event.urlAfterRedirects);
      });
  }

  private logPageView(url: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.lastTrackedPageUrl === url) return;
    this.lastTrackedPageUrl = url;

    const referrer = document.referrer;
    const loggedInUser = this.getLoggedInUserForTracking();
    const payload = {
      timestamp: new Date().toISOString(),
      module: 'page_view',
      eventAction: 'viewed',
      page_title: this.titleService.getTitle(),
      url,
      referrer,
      isOrganic: this.isOrganicVisitForTracking(referrer),
      visitor_id: localStorage.getItem('visitorId') || 'anonymous',
      user_id: this.getLoggedInUserIdForTracking(loggedInUser),
      user_name: loggedInUser?.user_name || '',
      user_email: loggedInUser?.email_id || loggedInUser?.email || '',
      eventData: {},
    };

    this.apiService.logEvent(payload).subscribe({ error: () => {} });
  }

  private getLoggedInUserForTracking(): any {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private getLoggedInUserIdForTracking(loggedInUser: any): string {
    const id = loggedInUser?.id ?? loggedInUser?.user_id ?? loggedInUser?._id ?? loggedInUser?.customer_id ?? loggedInUser?.userId;
    return id != null ? String(id) : '';
  }

  private isOrganicVisitForTracking(referrer: string): boolean {
    if (!referrer) return false;
    return ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'baidu.', 'yandex.'].some((domain) => referrer.includes(domain));
  }

  consoleWarn() {
    console.warn('%c WARNING %c This is a browser feature intended for developers. Using this console may allow attackers to impersonate you and steal your information using an attack called Self-XSS. Do not enter or paste code that you do not understand.',
      'color: red; background: yellow; font-size: 25px;',
      'color: #8ebf42;font-size:20px;'
    );
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('load', () => {
        const loadTime = performance.now() - this.startTime;
        console.log(`Total App Load Time: ${loadTime.toFixed(2)} ms (${(loadTime / 1000).toFixed(2)} s)`);
      });
    }
  }

  ngOnDestroy() {
    clearInterval(this.pollTimerId);
  }

  notifyClient(flag: boolean) { }

  setTimer() { }

  async updateApp(url?: string) {
    try {
      await this.updates.activateUpdate();
    } finally {
      if (url) {
        document.location.assign(url);
      } else {
        document.location.reload();
      }
    }
}

  updateSeoData(title: string, description: string, keywords: string): void {
    this.setMetaDescription(description);
    this.metaService.updateTag({ name: 'keywords', content: keywords })
  }

  setTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  setMetaDescription(description: string): void {
    const existingMeta = this.metaService.getTag("name='description'");
    if (existingMeta) {
      this.metaService.updateTag({ name: 'description', content: description });
    } else {
      this.metaService.addTag({ name: 'description', content: description });
    }
  }

}